import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

const OVERFLOW_BUFFER = 3

export default class extends Controller {
  static targets = [
    "input",
    "status",
    "add",
    "addTitle",
    "count",
    "countWrap",
    "limitMessage",
    "saveNote",
    "purge",
    "dropzone",
    "grid",
    "item",
    "coverBadge",
    "overflowBadge",
    "orderFields",
  ]

  static values = {
    imageLimit: Number,
    attachedCount: { type: Number, default: 0 },
    canReplace: Boolean,
  }

  connect() {
    this.dragDepth = 0
    this.pendingFiles = new Map()
    this.objectUrls = new Map()
    this.form = this.element.closest("form")
    this.boundSyncForSubmit = this.syncForSubmit.bind(this)
    this.form?.addEventListener("submit", this.boundSyncForSubmit, true)

    if (this.hasGridTarget) {
      this.sortable = Sortable.create(this.gridTarget, {
        animation: 150,
        draggable:
          ".listing-form__photo-item:not(.listing-form__photo-item--removed)",
        filter: ".listing-form__photo-add",
        preventOnFilter: false,
        onEnd: () => this.refreshIndicators(),
      })
    }

    this.refreshIndicators()
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.boundSyncForSubmit, true)
    this.sortable?.destroy()
    this.revokeObjectUrls()
  }

  preview() {
    const picked = Array.from(this.inputTarget.files).filter((file) =>
      file.type.startsWith("image/"),
    )
    this.inputTarget.value = ""

    if (!picked.length) return

    const trimmed = this.addPendingFiles(picked)
    if (trimmed && this.hasLimitMessageTarget) {
      this.limitMessageTarget.classList.remove("hidden")
      this.limitMessageTarget.textContent =
        this.limitMessageTarget.dataset.listingPhotosTrimmedMessageValue
    }
  }

  itemChanged(event) {
    const checkbox = event.target
    const item = checkbox.closest('[data-listing-photos-target="item"]')
    if (!item) return

    item.classList.toggle("listing-form__photo-item--removed", checkbox.checked)
    this.refreshIndicators()
  }

  dragOver(event) {
    if (!this.hasDropzoneTarget || !this.draggedFiles(event).length) return

    event.preventDefault()
    this.dragDepth += 1
    this.dropzoneTarget.classList.add("listing-form__photo--dragover")
  }

  dragLeave(event) {
    if (!this.hasDropzoneTarget) return

    event.preventDefault()
    this.dragDepth = Math.max(this.dragDepth - 1, 0)
    if (this.dragDepth === 0) {
      this.dropzoneTarget.classList.remove("listing-form__photo--dragover")
    }
  }

  drop(event) {
    if (!this.hasDropzoneTarget) return

    event.preventDefault()
    this.dragDepth = 0
    this.dropzoneTarget.classList.remove("listing-form__photo--dragover")

    const dropped = this.draggedFiles(event)
    if (!dropped.length) return

    const trimmed = this.addPendingFiles(dropped)
    if (trimmed && this.hasLimitMessageTarget) {
      this.limitMessageTarget.classList.remove("hidden")
      this.limitMessageTarget.textContent =
        this.limitMessageTarget.dataset.listingPhotosTrimmedMessageValue
    }
  }

  draggedFiles(event) {
    return Array.from(event.dataTransfer?.files || []).filter((file) =>
      file.type.startsWith("image/"),
    )
  }

  addPendingFiles(files) {
    const room = this.maxPickerTotal() - this.activeItems().length
    if (room <= 0) return files.length > 0

    const accepted = files.slice(0, room)
    accepted.forEach((file) => {
      const id = crypto.randomUUID()
      this.pendingFiles.set(id, file)
      this.gridTarget.insertBefore(
        this.buildPendingItem(id, file),
        this.addTarget,
      )
    })

    this.refreshIndicators()
    return accepted.length < files.length
  }

  buildPendingItem(id, file) {
    const url = URL.createObjectURL(file)
    this.objectUrls.set(id, url)

    const item = document.createElement("div")
    item.className =
      "listing-form__photo-item listing-form__photo-item--pending"
    item.dataset.listingPhotosTarget = "item"
    item.dataset.photoKind = "pending"
    item.dataset.clientId = id

    item.innerHTML = `
      <div class="listing-form__photo-thumb-wrap">
        <img src="${url}" alt="" class="listing-form__photo-thumb">
        <span class="listing-form__photo-cover-badge" data-listing-photos-target="coverBadge">Cover</span>
        <span class="listing-form__photo-overflow-badge" data-listing-photos-target="overflowBadge">
          <span class="listing-form__photo-overflow-x" aria-hidden="true">×</span>
          <span class="listing-form__photo-overflow-label">Won't save</span>
        </span>
      </div>
      <button type="button" class="listing-form__photo-remove listing-form__photo-remove--button" data-action="listing-photos#removePending">
        <span>Remove</span>
      </button>
    `

    return item
  }

  removePending(event) {
    const item = event.currentTarget.closest(
      '[data-listing-photos-target="item"]',
    )
    if (!item) return

    const id = item.dataset.clientId
    this.dropPendingItem(id)
    item.remove()
    this.refreshIndicators()
  }

  dropPendingItem(id) {
    if (!id) return

    this.pendingFiles.delete(id)
    const url = this.objectUrls.get(id)
    if (url) {
      URL.revokeObjectURL(url)
      this.objectUrls.delete(id)
    }
  }

  activeItems() {
    return this.itemTargets.filter(
      (item) => !item.classList.contains("listing-form__photo-item--removed"),
    )
  }

  maxPickerTotal() {
    if (!this.hasImageLimitValue) return Infinity

    return this.imageLimitValue + OVERFLOW_BUFFER
  }

  saveLimit() {
    if (!this.hasImageLimitValue) return Infinity

    return this.imageLimitValue
  }

  refreshIndicators() {
    const active = this.activeItems()
    const limit = this.saveLimit()
    const savedCount =
      limit === Infinity ? active.length : Math.min(active.length, limit)
    const overflowCount =
      limit === Infinity ? 0 : Math.max(active.length - limit, 0)

    active.forEach((item, index) => {
      const overflow = limit !== Infinity && index >= limit
      item.classList.toggle("listing-form__photo-item--overflow", overflow)
      item.classList.toggle("listing-form__photo-item--cover", index === 0)

      const badge = item.querySelector(
        '[data-listing-photos-target="coverBadge"]',
      )
      if (badge) badge.hidden = index !== 0 || overflow

      const overflowBadge = item.querySelector(
        '[data-listing-photos-target="overflowBadge"]',
      )
      if (overflowBadge) overflowBadge.hidden = !overflow
    })

    if (this.hasCountTarget) {
      this.countTarget.textContent = String(savedCount)
    }

    if (this.hasSaveNoteTarget) {
      if (overflowCount > 0) {
        this.saveNoteTarget.hidden = false
        this.saveNoteTarget.textContent = `${overflowCount} queued photo${overflowCount === 1 ? "" : "s"} won't save — drag above the line or remove them.`
      } else {
        this.saveNoteTarget.hidden = true
        this.saveNoteTarget.textContent = ""
      }
    }

    if (this.hasAddTarget) {
      const atPickerCap = active.length >= this.maxPickerTotal()
      this.addTarget.hidden = atPickerCap
      this.addTarget.classList.toggle(
        "listing-form__photo-add--solo",
        active.length === 0,
      )
    }

    if (this.hasStatusTarget) {
      if (active.length === 0) {
        this.statusTarget.textContent = "Tap to upload · drag to reorder"
      } else if (overflowCount > 0) {
        this.statusTarget.textContent = `${savedCount} will save · ${overflowCount} won't`
      } else if (active.length === 1) {
        this.statusTarget.textContent = "1 photo ready · drag to reorder"
      } else {
        this.statusTarget.textContent = `${active.length} photos · drag to reorder`
      }
    }
  }

  syncForSubmit() {
    const limit = this.saveLimit()
    const active = this.activeItems()
    const saved = limit === Infinity ? active : active.slice(0, limit)

    if (this.hasPurgeTarget) {
      this.purgeTargets.forEach((checkbox) => {
        const item = checkbox.closest('[data-listing-photos-target="item"]')
        const removed = item?.classList.contains(
          "listing-form__photo-item--removed",
        )
        const kept = item && saved.includes(item)
        checkbox.checked = Boolean(removed || !kept)
      })
    }

    const files = saved
      .filter((item) => item.dataset.photoKind === "pending")
      .map((item) => this.pendingFiles.get(item.dataset.clientId))
      .filter(Boolean)

    this.syncInputFiles(files)
    this.syncOrderFields(saved)
  }

  syncOrderFields(saved) {
    if (!this.hasOrderFieldsTarget) return

    this.orderFieldsTarget.replaceChildren()

    saved.forEach((item) => {
      const orderInput = document.createElement("input")
      orderInput.type = "hidden"
      orderInput.name = "listing[image_order][]"
      orderInput.value =
        item.dataset.photoKind === "pending"
          ? `pending:${item.dataset.clientId}`
          : `existing:${item.dataset.attachmentId}`
      this.orderFieldsTarget.appendChild(orderInput)
    })

    saved
      .filter((item) => item.dataset.photoKind === "pending")
      .forEach((item) => {
        const keyInput = document.createElement("input")
        keyInput.type = "hidden"
        keyInput.name = "listing[pending_image_keys][]"
        keyInput.value = item.dataset.clientId
        this.orderFieldsTarget.appendChild(keyInput)
      })
  }

  syncInputFiles(files) {
    const transfer = new window.DataTransfer()
    files.forEach((file) => transfer.items.add(file))
    this.inputTarget.files = transfer.files
  }

  revokeObjectUrls() {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url))
    this.objectUrls.clear()
  }
}
